import { z } from 'zod'

/**
 * Every VITE_* variable is compiled into files anyone can read. Nothing secret
 * belongs here — API keys and signing secrets stay on the backend.
 *
 * Parsing at module scope means a missing variable fails at boot, in the
 * developer's face, instead of as `undefined` in production.
 */
const envSchema = z
  .object({
    VITE_API_URL: z.url(),
    /** The realtime service's REST base. A different port, so a separate base. */
    VITE_REALTIME_API_URL: z.url().optional(),
    /** Stream endpoint. Absent means the project has no realtime layer. */
    VITE_REALTIME_URL: z.string().optional(),

    /**
     * goauth origin. Empty means same-origin — the dev server proxies `/auth`
     * to it, which is how the browser avoids CORS entirely.
     */
    VITE_GOAUTH_URL: z.string().default(''),

    VITE_APP_NAME: z.string().min(1).default('PAPAY Cloaking'),
    /**
     * Lets the panel render before an auth backend exists, so UI work is not
     * blocked by a login wall. Honoured in development builds only — see
     * `features/auth`.
     */
    VITE_AUTH_DISABLED: z
      .string()
      .optional()
      .transform((value) => value === 'true'),
  })

const parsed = envSchema.safeParse(import.meta.env)

if (!parsed.success) {
  const details = parsed.error.issues
    .map((issue) => `  ${issue.path.join('.')}: ${issue.message}`)
    .join('\n')
  throw new Error(`Invalid environment. Check your .env file:\n${details}`)
}

export const env = {
  ...parsed.data,
  VITE_REALTIME_API_URL: parsed.data.VITE_REALTIME_API_URL ?? parsed.data.VITE_API_URL,
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const
